import {
  importProvidersFrom,
  inject,
  provideEnvironmentInitializer,
} from '@angular/core';
import {
  ApplicationStore,
  getModuleGroupById,
  SOFTLINE_CONFIG_OFFLINE_FEATURE,
  SOFTLINE_FEATURE_APPLICATIONS,
} from '@softline/application';
import {
  SOFTLINE_SERVICE_HTTP,
  SOFTLINE_STORE_ENTITY_SERVICE,
  SoftlineEntityDetailService,
  SoftlineEntityService,
  Store,
  StoreModule,
} from '@softline/core';
import { SOFTLINE_FEATURE_TRANSLATION, TranslationStore } from '@softline/ui-core';
import {
  provideSoftAppsModule,
  withLazySoftAppsModuleTranslation,
  withSoftAppsModuleInfo,
  withSoftAppsModuleRoutes,
} from '@softapps/core';
import { default as de } from '../i18n/de.json';
import { provideBestellvorschlagGlobals } from './bestellvorschlag.globals';
import { bestellvorschlagRoutes } from './bestellvorschlag.routes';
import {
  SOFTLINE_API_MDE_BESTELLVORSCHLAG_ARTIKEL,
  SOFTLINE_API_MDE_BESTELLVORSCHLAG_BEWEGUNGEN,
} from './bestellvorschlag.api';
import {
  SOFTLINE_FEATURE_MDE_BESTELLVORSCHLAG,
  SOFTLINE_FEATURE_MDE_BESTELLVORSCHLAG_ARTIKEL_DETAILS,
  SOFTLINE_FEATURE_MDE_BESTELLVORSCHLAG_BEWE,
  SOFTLINE_PERMISSION_MDE_BESTELLVORSCHLAG,
} from './bestellvorschlag.shared';
import { BestellvorschlagResolver } from './resolver/bestellvorschlag.resolver';
import { BestellvorschlagService } from './services/bestellvorschlag.service';
import { BestellvorschlagArtikelDetailStore } from './store/bestellvorschlag-artikel-detail.store';
import { BestellvorschlagBeweStore } from './store/bestellvorschlag-bewe.store';
import { BestellvorschlagStore } from './store/bestellvorschlag.store';

export const bestellvorschlagProviders = [
  provideEnvironmentInitializer(async () => {
    const store = inject(Store);
    store.commit(SOFTLINE_FEATURE_TRANSLATION, TranslationStore.mutations.add, {
      module: 'mdeBestellvorschlag',
      language: 'de',
      translations: de,
    });

    store.commit(SOFTLINE_FEATURE_APPLICATIONS, ApplicationStore.mutations.add, {
      name: 'Bestellvorschlag',
      icon: 'fa-regular fa-cart-arrow-down',
      route: '/mde-bestellvorschlag',
      permission: SOFTLINE_PERMISSION_MDE_BESTELLVORSCHLAG,
      group: getModuleGroupById(6),
    });

    store.commit(
      SOFTLINE_FEATURE_MDE_BESTELLVORSCHLAG_BEWE,
      BestellvorschlagBeweStore.mutations.setSort,
      {
        property: 'id',
        title: 'Erfassungszeitpunkt',
        direction: 'desc',
      } as any
    );
  }),
  importProvidersFrom(
    StoreModule.forFeature({
      name: SOFTLINE_FEATURE_MDE_BESTELLVORSCHLAG,
      feature: BestellvorschlagStore.feature,
      providers: [
        {
          provide: BestellvorschlagService,
          useClass: BestellvorschlagService,
        },
      ],
    }),
    StoreModule.forFeature({
      name: SOFTLINE_FEATURE_MDE_BESTELLVORSCHLAG_BEWE,
      feature: BestellvorschlagBeweStore.feature,
      providers: [
        {
          provide: SOFTLINE_STORE_ENTITY_SERVICE,
          useFactory: () =>
            new SoftlineEntityService(
              inject(SOFTLINE_SERVICE_HTTP),
              SOFTLINE_API_MDE_BESTELLVORSCHLAG_BEWEGUNGEN
            ),
        },
      ],
    })
  ),
  importProvidersFrom(
    StoreModule.forFeature({
      name: SOFTLINE_FEATURE_MDE_BESTELLVORSCHLAG_ARTIKEL_DETAILS,
      feature: BestellvorschlagArtikelDetailStore.feature,
      providers: [
        {
          provide: SOFTLINE_STORE_ENTITY_SERVICE,
          useFactory: () => {
            return new SoftlineEntityDetailService(
              inject(SOFTLINE_SERVICE_HTTP),
              SOFTLINE_API_MDE_BESTELLVORSCHLAG_ARTIKEL,
              'detail'
            );
          },
        },
      ],
    })
  ),
  BestellvorschlagResolver,
  {
    provide: SOFTLINE_CONFIG_OFFLINE_FEATURE,
    useValue: { featureName: SOFTLINE_FEATURE_MDE_BESTELLVORSCHLAG, fields: ['datum'] },
    multi: true,
  },
];

export function provideBestellvorschlagModule() {
  return [
    provideSoftAppsModule(
      {
        name: 'mdeBestellvorschlag',
        title: 'Bestellvorschlag',
        icon: 'fa-regular fa-cart-arrow-down',
        route: '/mde-bestellvorschlag',
        permission: SOFTLINE_PERMISSION_MDE_BESTELLVORSCHLAG,
        group: getModuleGroupById(6),
      },
      withSoftAppsModuleRoutes({ app: bestellvorschlagRoutes }),
      withLazySoftAppsModuleTranslation({
        lazy: { de: () => import('../i18n/de.json') },
      }),
      withSoftAppsModuleInfo({
        library: '@softapps/mde/bestellvorschlag',
        remoteConfig: 'mde-bestellvorschlag',
        description: 'Bestellvorschlag am MDE-Gerät bearbeiten.',
      })
    ),
    ...provideBestellvorschlagGlobals(),
  ];
}
